import type {ClientIntegrationInfo, IntegrationWithClients} from "$lib/models/integration/integration";

import {fetchWithAuth} from "$lib/utils/http/httpUtil";
import {writable} from "svelte/store";

export const integrationsWithClients = writable<IntegrationWithClients[]>([]);

function isAbortError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && 'name' in error && (error as { name: string }).name === 'AbortError';
}

export function createAliiConnectTypeStore() {
  return {
    async fetchAllIntegrationsWithClients(signal: AbortSignal) {
      try {
        const response = await fetchWithAuth('/api/integration/integrations-with-clients', { signal });

        if (!response.ok) {
          throw new Error(`Failed to fetch integrations (HTTP ${response.status})`);
        }

        const data = (await response.json()) as IntegrationWithClients[];
        data.sort((a, b) => a.integrationTypeName.localeCompare(b.integrationTypeName));
        integrationsWithClients.set(data);
        
        return data;
      } catch (err) {
        if (isAbortError(err)) {
          console.log('Fetch integrations aborted');
          return null;
        }
        throw err;
      }
    },

    getClientsForIntegration(integrationTypeName: string): ClientIntegrationInfo[] {
      let result: ClientIntegrationInfo[] = [];
      integrationsWithClients.subscribe(integrations => {
        const integration = integrations.find(i => i.integrationTypeName === integrationTypeName);
        if (integration) {
          result = integration.clients;
        }
      })();
      return result;
    },

    getSyncTypesForIntegration(integrationTypeName: string): string[] {
      const clients = this.getClientsForIntegration(integrationTypeName);
      const uniqueSyncTypes = new Set<string>();

      for (const client of clients) {
        for (const entity of client.entities) {
          for (const syncTypeStatus of entity.syncTypes) {
            uniqueSyncTypes.add(syncTypeStatus.syncTypeName);
          }
        }
      }

      return Array.from(uniqueSyncTypes).sort();
    }
  };
}

export const aliiIntegrationStore = createAliiConnectTypeStore();